/**
 * InstallAppButton — one-click PWA install for Chromium browsers, and
 * a friendly "how to install" fallback for everyone else.
 *
 * Behavior tiers:
 * 1. Chromium (Android Chrome, desktop Chrome/Edge, Samsung Internet):
 *    listen for `beforeinstallprompt`, then fire the native prompt on
 *    click.
 * 2. iOS Safari: no beforeinstallprompt event exists. Show a modal
 *    with the Share → Add to Home Screen instructions.
 * 3. Other browsers (Firefox desktop/mobile, macOS Safari, in-app
 *    webviews): show a generic "How to install" modal that adapts
 *    based on the detected browser/OS.
 *
 * Auto-hides when the app is already installed (running as standalone
 * PWA) and after a successful `appinstalled` event.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Download, Share, PlusSquare, X, MoreVertical, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

function isStandalone() {
  if (typeof window === 'undefined') return false;
  const iosStandalone = window.navigator && window.navigator.standalone === true;
  const mqStandalone = typeof window.matchMedia === 'function'
    && window.matchMedia('(display-mode: standalone)').matches;
  return Boolean(iosStandalone || mqStandalone);
}

function detectPlatform() {
  if (typeof window === 'undefined') return { os: 'unknown', browser: 'unknown' };
  const ua = (window.navigator.userAgent || '').toLowerCase();
  // OS
  let os = 'unknown';
  if (/iphone|ipad|ipod/.test(ua)) os = 'ios';
  else if (/macintosh/.test(ua) && 'ontouchend' in document) os = 'ios'; // iPadOS 13+
  else if (/android/.test(ua)) os = 'android';
  else if (/windows/.test(ua)) os = 'windows';
  else if (/macintosh|mac os x/.test(ua)) os = 'macos';
  else if (/linux/.test(ua)) os = 'linux';
  // Browser (order matters — Edge/Opera/Samsung are Chromium-based)
  let browser = 'other';
  if (/edg\//.test(ua)) browser = 'edge';
  else if (/samsungbrowser/.test(ua)) browser = 'samsung';
  else if (/opr\/|opera/.test(ua)) browser = 'opera';
  else if (/firefox|fxios/.test(ua)) browser = 'firefox';
  else if (/chrome\/|crios/.test(ua)) browser = 'chrome';
  else if (/safari/.test(ua)) browser = 'safari';
  return { os, browser };
}

export default function InstallAppButton({ className = '', variant = 'default' }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(() => isStandalone());
  const [showInstructions, setShowInstructions] = useState(false);
  const { os, browser } = detectPlatform();

  useEffect(() => {
    if (installed) return;
    const beforeHandler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const installedHandler = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      toast.success('Schedule Guru added to your device.');
    };
    const displayModeHandler = (mql) => { if (mql.matches) setInstalled(true); };
    window.addEventListener('beforeinstallprompt', beforeHandler);
    window.addEventListener('appinstalled', installedHandler);
    let mql = null;
    try {
      mql = window.matchMedia('(display-mode: standalone)');
      // Some browsers (older Safari) don't support addEventListener on MediaQueryList.
      if (mql.addEventListener) mql.addEventListener('change', displayModeHandler);
      else if (mql.addListener) mql.addListener(displayModeHandler);
    } catch { /* ignore */ }
    return () => {
      window.removeEventListener('beforeinstallprompt', beforeHandler);
      window.removeEventListener('appinstalled', installedHandler);
      if (mql) {
        if (mql.removeEventListener) mql.removeEventListener('change', displayModeHandler);
        else if (mql.removeListener) mql.removeListener(displayModeHandler);
      }
    };
  }, [installed]);

  const handleClick = useCallback(async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice && choice.outcome === 'accepted') {
          setInstalled(true);
          toast.success('Installing Schedule Guru…');
        }
      } catch {
        toast.error("Couldn't launch the install prompt — try again from your browser menu.");
      } finally {
        setDeferredPrompt(null);
      }
      return;
    }
    setShowInstructions(true);
  }, [deferredPrompt]);

  if (installed) return null;

  const STYLES = {
    hero: { size: 'lg', variant: 'outline', cls: 'bg-emerald-400/15 border-emerald-300/40 text-emerald-100 hover:bg-emerald-400/25 hover:text-white h-14 px-8 text-base font-bold rounded-md', icon: 'w-5 h-5 mr-2' },
    'hero-compact': { size: 'sm', variant: 'ghost', cls: 'text-emerald-200 hover:text-white hover:bg-white/10', icon: 'w-4 h-4 mr-1.5' },
  };
  const style = STYLES[variant] || { size: 'default', variant: 'default', cls: 'bg-white text-[#00247D] hover:bg-slate-100', icon: 'w-5 h-5 mr-2' };

  return (
    <>
      <Button
        size={style.size}
        variant={style.variant}
        onClick={handleClick}
        className={`${style.cls} ${className}`}
        data-testid="install-app-btn"
      >
        <Download className={style.icon} />
        Install App
      </Button>

      {showInstructions && createPortal(
        <InstallInstructionsModal
          os={os}
          browser={browser}
          onClose={() => setShowInstructions(false)}
        />,
        document.body,
      )}
    </>
  );
}

/**
 * Renders platform-aware instructions when the native prompt isn't
 * available. The parent chooses when to show it.
 */
function InstallInstructionsModal({ os, browser, onClose }) {
  const steps = buildSteps(os, browser);
  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/60 flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="install-instructions-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-600"
          aria-label="Close"
          data-testid="install-instructions-close"
        >
          <X className="w-5 h-5" />
        </button>
        <h3
          className="text-xl font-extrabold text-[#00247D] mb-1"
          style={{ fontFamily: 'Barlow Condensed' }}
          data-testid="install-instructions-title"
        >
          {steps.title}
        </h3>
        <p className="text-sm text-slate-500 mb-4">{steps.subtitle}</p>
        <ol className="space-y-3 text-sm text-slate-700">
          {steps.items.map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00247D] text-white text-xs font-bold flex items-center justify-center">
                {i + 1}
              </span>
              <span className="leading-relaxed">{item}</span>
            </li>
          ))}
        </ol>
        {steps.footer && (
          <p className="mt-5 text-xs text-slate-400 flex items-start gap-2">
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <span>{steps.footer}</span>
          </p>
        )}
      </div>
    </div>
  );
}

function buildSteps(os, browser) {
  const shareIcon = <Share className="w-4 h-4 inline-block align-text-bottom text-[#00247D]" aria-hidden="true" />;
  const addIcon = <PlusSquare className="w-4 h-4 inline-block align-text-bottom text-[#00247D]" aria-hidden="true" />;
  const dotsIcon = <MoreVertical className="w-4 h-4 inline-block align-text-bottom text-[#00247D]" aria-hidden="true" />;
  if (os === 'ios') {
    if (browser !== 'safari' && browser !== 'other') {
      return {
        title: 'Open in Safari to install',
        subtitle: 'On iPhone and iPad only Safari can install web apps.',
        items: [
          <>Tap the share menu in this browser and choose <strong>Open in Safari</strong>.</>,
          <>Once in Safari, tap {shareIcon} <strong>Share</strong> → {addIcon} <strong>Add to Home Screen</strong>.</>,
        ],
        footer: 'Chrome/Firefox on iOS can\'t install web apps due to Apple restrictions.',
      };
    }
    return {
      title: 'Install on iPhone / iPad',
      subtitle: 'Add Schedule Guru to your Home Screen in two taps.',
      items: [
        <>Tap the {shareIcon} <strong>Share</strong> button in Safari's toolbar.</>,
        <>Choose {addIcon} <strong>Add to Home Screen</strong>, then tap <em>Add</em>.</>,
      ],
    };
  }
  if (os === 'android') {
    if (browser === 'firefox') {
      return {
        title: 'Install on Android',
        subtitle: 'Firefox for Android supports web apps.',
        items: [
          <>Tap the {dotsIcon} <strong>menu</strong> button (top-right).</>,
          <>Choose <strong>Install</strong> or <strong>Add to Home Screen</strong>.</>,
        ],
      };
    }
    return {
      title: 'Install on Android',
      subtitle: 'Adds Schedule Guru to your Home Screen and app drawer.',
      items: [
        <>Tap the {dotsIcon} <strong>menu</strong> button in your browser.</>,
        <>Choose <strong>Install app</strong> (or <strong>Add to Home Screen</strong>), then confirm.</>,
      ],
    };
  }
  // Desktop — Chromium: address-bar install icon; Firefox/Safari: bookmark.
  if (browser === 'firefox') {
    return {
      title: 'Install on Firefox',
      subtitle: 'Firefox desktop doesn\'t support installing web apps yet.',
      items: [
        <>Bookmark this page (<strong>Ctrl / ⌘ + D</strong>) for quick access.</>,
        <>To get a real installable app, try opening Schedule Guru in <strong>Chrome</strong> or <strong>Edge</strong>.</>,
      ],
      footer: 'Firefox may add web app install support in a future release.',
    };
  }
  if (browser === 'safari' && os === 'macos') {
    return {
      title: 'Install on macOS Safari',
      subtitle: 'Safari 17+ can add web apps to your Dock.',
      items: [
        <>Click the <strong>File</strong> menu in Safari's menu bar.</>,
        <>Choose <strong>Add to Dock</strong>, then click <em>Add</em>.</>,
      ],
      footer: 'Requires macOS Sonoma (14) or newer with Safari 17.',
    };
  }
  // Chromium desktop — Chrome / Edge / Opera / Brave / Samsung.
  return {
    title: 'Install on Desktop',
    subtitle: 'Adds Schedule Guru as a standalone desktop app.',
    items: [
      <>Look for the install icon in the address bar (usually a small screen with a down arrow) and click <strong>Install</strong>.</>,
      <>Or open your browser's {dotsIcon} <strong>menu</strong> → <strong>Install Schedule Guru</strong> (Chrome/Edge/Brave).</>,
    ],
    footer: 'If the install option isn\'t visible yet, try refreshing this page once — Chromium needs a moment to recognize the app.',
  };
}
